import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, StatCard } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Table, Td, Th } from "@/components/ui/Table";
import { useReference } from "@/hooks/useReference";
import { useToast } from "@/hooks/useToast";
import { teams as teamsApi } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { strings } from "@/lib/strings";
import type { Team } from "@/lib/types";
import {
  DEFAULT_DEDUP,
  DEFAULT_PARAMS,
  analysis,
  type AnalysisFilters,
  type AnalysisPreview,
  type AnalysisRun,
  type AnalysisRunDetail,
  type AntibiogramParams,
  type DedupRule,
} from "@/lib/analysis";
import { AntibiogramTable } from "./AntibiogramTable";
import { PhenotypePanel } from "./PhenotypePanel";

/**
 * The cumulative antibiogram screen (docs/phase2, AN-xx).
 *
 * Preview first, then produce. The preview exists so the microbiologist sees
 * the de-duplication coverage before committing to a report: a coverage of 60%
 * means most rows had no patient number and the figures are closer to an
 * isolate count than a patient count, which changes how they should be read.
 */
export function AnalysisPage() {
  const { notify } = useToast();
  const { specimens, organisms, wards } = useReference();

  const [title, setTitle] = useState("");
  const [filters, setFilters] = useState<AnalysisFilters>({});
  const [dedup, setDedup] = useState<DedupRule>(DEFAULT_DEDUP);
  const [params, setParams] = useState<AntibiogramParams>(DEFAULT_PARAMS);

  const [preview, setPreview] = useState<AnalysisPreview | null>(null);
  const [current, setCurrent] = useState<AnalysisRunDetail | null>(null);
  const [history, setHistory] = useState<AnalysisRun[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [busy, setBusy] = useState(false);

  const body = useCallback(
    () => ({ title, filters, dedup, antibiogram: params }),
    [title, filters, dedup, params],
  );

  const refreshPreview = useCallback(async () => {
    try {
      setPreview(await analysis.preview({ filters, dedup, antibiogram: params }));
    } catch {
      setPreview(null);
    }
  }, [filters, dedup, params]);

  const loadHistory = useCallback(async () => {
    try {
      setHistory(await analysis.listRuns());
    } catch {
      // History is a convenience; failing to load it is not fatal.
    }
  }, []);

  useEffect(() => {
    void refreshPreview();
  }, [refreshPreview]);

  useEffect(() => {
    void loadHistory();
    void teamsApi.list().then(setTeams).catch(() => undefined);
  }, [loadHistory]);

  const produce = async () => {
    setBusy(true);
    try {
      const run = await analysis.createRun(body());
      setCurrent(run);
      notify(
        `Antibiogram produced from ${run.isolates_included} isolates.`,
        "success",
      );
      await loadHistory();
    } catch {
      notify(strings.errors.generic, "error");
    } finally {
      setBusy(false);
    }
  };

  const open = async (id: string) => {
    try {
      setCurrent(await analysis.getRun(id));
    } catch {
      notify(strings.errors.notFound, "error");
    }
  };

  const setFilter = <K extends keyof AnalysisFilters>(
    key: K,
    value: AnalysisFilters[K],
  ) => setFilters((c) => ({ ...c, [key]: value }));

  const coveragePercent =
    preview === null ? null : Math.round(preview.dedup.coverage * 100);
  const lowCoverage = coveragePercent !== null && coveragePercent < 80;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">
          Cumulative antibiogram
        </h1>
        <p className="text-sm text-slate-500">
          One isolate per patient per organism, following CLSI M39. WHONET
          remains available for deeper analysis.
        </p>
      </div>

      <Card>
        <CardHeader
          title="What to include"
          description="Only complete isolates with no unresolved blocking flag, the same rule the BacLink export applies."
        />
        <CardBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field id="title" label="Report title">
              <Input
                id="title"
                placeholder="2026 cumulative antibiogram"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </Field>
            <Field id="from" label="Specimen date from">
              <Input
                id="from"
                type="date"
                value={filters.date_from ?? ""}
                onChange={(e) => setFilter("date_from", e.target.value || null)}
              />
            </Field>
            <Field id="to" label="Specimen date to">
              <Input
                id="to"
                type="date"
                value={filters.date_to ?? ""}
                onChange={(e) => setFilter("date_to", e.target.value || null)}
              />
            </Field>
            <Field id="team" label="Team">
              <Select
                id="team"
                placeholder="All teams"
                value={filters.team ?? ""}
                onChange={(e) => setFilter("team", e.target.value || null)}
                options={teams.map((t) => ({ value: t.slug, label: t.name }))}
              />
            </Field>
            <Field id="specimen" label="Specimen type">
              <Select
                id="specimen"
                placeholder="All specimens"
                value={filters.specimen_type ?? ""}
                onChange={(e) => setFilter("specimen_type", e.target.value || null)}
                options={specimens.map((s) => ({ value: s.code, label: s.name }))}
              />
            </Field>
            <Field
              id="location"
              label="Patient location"
              hint="Stratify inpatient against outpatient."
            >
              <Select
                id="location"
                placeholder="Anywhere"
                value={filters.location_type ?? ""}
                onChange={(e) => setFilter("location_type", e.target.value || null)}
                options={[
                  { value: "inx", label: "Inpatient" },
                  { value: "icu", label: "Intensive care" },
                  { value: "out", label: "Outpatient" },
                  { value: "eme", label: "Emergency" },
                ]}
              />
            </Field>
            <Field id="ward" label="Ward">
              <Select
                id="ward"
                placeholder="All wards"
                value={filters.ward_code ?? ""}
                onChange={(e) => setFilter("ward_code", e.target.value || null)}
                options={wards.map((w) => ({ value: w.code, label: w.name }))}
              />
            </Field>
            <Field id="organism" label="Organism">
              <Select
                id="organism"
                placeholder="All organisms"
                value={filters.organism_code ?? ""}
                onChange={(e) => setFilter("organism_code", e.target.value || null)}
                options={organisms.map((o) => ({ value: o.code, label: o.name }))}
              />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Reporting conventions"
          description="Labs genuinely differ on these, so both are recorded on the report and can be reproduced later."
        />
        <CardBody className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            id="dedup-scope"
            label="Count one isolate per"
            hint="M39 counts the first isolate per patient per organism."
          >
            <Select
              id="dedup-scope"
              value={dedup.scope}
              onChange={(e) =>
                setDedup({ ...dedup, scope: e.target.value as DedupRule["scope"] })
              }
              options={[
                { value: "patient_species", label: "Patient and organism" },
                {
                  value: "patient_species_specimen",
                  label: "Patient, organism and specimen type",
                },
              ]}
            />
          </Field>

          <Field
            id="episode"
            label="New episode after (days)"
            hint="Blank counts each patient once for the whole period."
          >
            <Input
              id="episode"
              type="number"
              min={1}
              max={365}
              value={dedup.episode_days ?? ""}
              onChange={(e) =>
                setDedup({
                  ...dedup,
                  episode_days: e.target.value === "" ? null : Number(e.target.value),
                })
              }
            />
          </Field>

          <Field
            id="threshold"
            label="Hide % below n"
            hint="CLSI M39 recommends 30."
          >
            <Input
              id="threshold"
              type="number"
              min={0}
              max={1000}
              value={params.suppression_threshold}
              onChange={(e) =>
                setParams({
                  ...params,
                  suppression_threshold: Number(e.target.value || 0),
                })
              }
            />
          </Field>

          <Field id="numerator" label="Count as susceptible">
            <Select
              id="numerator"
              value={params.numerator}
              onChange={(e) =>
                setParams({
                  ...params,
                  numerator: e.target.value as AntibiogramParams["numerator"],
                })
              }
              options={[
                { value: "s_only", label: "Susceptible only (M39)" },
                { value: "s_and_i", label: "Susceptible or intermediate" },
              ]}
            />
          </Field>
        </CardBody>
      </Card>

      {preview && (
        <Card>
          <CardHeader title="Before you produce it" />
          <CardBody className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Isolates considered" value={preview.dedup.total} />
              <StatCard
                label="After de-duplication"
                value={preview.dedup.kept}
                hint={`${preview.dedup.removed} repeat isolates excluded`}
              />
              <StatCard label="Distinct patients" value={preview.distinct_patients} />
              <StatCard
                label="Patient-number coverage"
                value={coveragePercent === null ? "—" : `${coveragePercent}%`}
                tone={lowCoverage ? "warning" : "ok"}
              />
            </div>

            {lowCoverage && (
              <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                Only {coveragePercent}% of these isolates carry a hospital number,
                so the rest could not be de-duplicated and each counts as its own
                patient. The figures are therefore closer to an isolate count than
                a patient count. Worth noting on the report, and worth chasing in
                the registers.
              </p>
            )}

            {preview.total_cells > 0 &&
              preview.suppressed_cells === preview.total_cells && (
                <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                  Every cell has fewer than {params.suppression_threshold} isolates,
                  so no percentage can be reported yet. The counts are still shown.
                </p>
              )}

            <Button loading={busy} onClick={() => void produce()}>
              Produce the antibiogram
            </Button>
          </CardBody>
        </Card>
      )}

      {current && (
        <>
          <Card>
            <CardHeader
              title={current.title || "Antibiogram"}
              description={`${current.isolates_included} isolates from ${current.distinct_patients} patients · produced ${formatDateTime(current.created_at)}`}
              actions={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    void analysis.download(
                      current.id,
                      `eksuth-antibiogram-${current.created_at.slice(0, 10)}.csv`,
                    )
                  }
                >
                  Download CSV
                </Button>
              }
            />
            <CardBody>
              <AntibiogramTable matrix={current.result.antibiogram} />
            </CardBody>
          </Card>

          <PhenotypePanel phenotypes={current.result.phenotypes} />
        </>
      )}

      <Card>
        <CardHeader
          title="Previous reports"
          description="Each stores the conventions that produced it, so a figure can be traced and reproduced."
        />
        {history.length === 0 ? (
          <EmptyState title="No antibiograms produced yet." />
        ) : (
          <Table caption="Previous antibiograms">
            <thead>
              <tr>
                <Th>Produced</Th>
                <Th>Title</Th>
                <Th className="text-right">Isolates</Th>
                <Th className="text-right">Coverage</Th>
                <Th>Fingerprint</Th>
                <Th>
                  <span className="sr-only">Open</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {history.map((run) => (
                <tr key={run.id} className="hover:bg-slate-50">
                  <Td className="whitespace-nowrap text-xs">
                    {formatDateTime(run.created_at)}
                  </Td>
                  <Td>{run.title || <span className="text-slate-400">untitled</span>}</Td>
                  <Td className="text-right tabular-nums">{run.isolates_included}</Td>
                  <Td className="text-right tabular-nums">
                    <Badge tone={run.dedup_coverage < 0.8 ? "warning" : "ok"}>
                      {Math.round(run.dedup_coverage * 100)}%
                    </Badge>
                  </Td>
                  <Td className="font-mono text-xs">
                    <span className="block max-w-[10rem] truncate" title={run.result_hash ?? ""}>
                      {run.result_hash?.slice(0, 12) ?? "—"}
                    </span>
                  </Td>
                  <Td>
                    <Button variant="ghost" size="sm" onClick={() => void open(run.id)}>
                      Open
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
